import { gql, WatchQueryFetchPolicy } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  SnoozedEmail,
  SnoozeEmailInput,
  UpdateSnoozeInput,
} from '@this+that/models';

import { FETCH_POLICY_CACHE_FIRST } from '../utils/constants';

const SNOOZED_EMAIL_FIELDS = gql`
  fragment SnoozedEmailFields on SnoozedEmail {
    userId
    snoozeId
    threadId
    messageId
    internetMessageId
    platform
    userEmailAddress
    snoozeUntil
    markUnreadOnWake
    createdAt
    updatedAt
  }
`;

export const GET_SNOOZED_EMAILS = gql`
  ${SNOOZED_EMAIL_FIELDS}
  query snoozedEmails {
    snoozedEmails {
      ...SnoozedEmailFields
    }
  }
`;

const SNOOZE_EMAIL = gql`
  ${SNOOZED_EMAIL_FIELDS}
  mutation snoozeEmail($input: SnoozeEmailInput!) {
    snoozeEmail(input: $input) {
      ...SnoozedEmailFields
    }
  }
`;

const UPDATE_SNOOZE = gql`
  ${SNOOZED_EMAIL_FIELDS}
  mutation updateSnooze($input: UpdateSnoozeInput!) {
    updateSnooze(input: $input) {
      ...SnoozedEmailFields
    }
  }
`;

const UNSNOOZE_EMAIL = gql`
  mutation unsnoozeEmail($snoozeId: String!) {
    unsnoozeEmail(snoozeId: $snoozeId)
  }
`;

interface SnoozedEmailsData {
  snoozedEmails: SnoozedEmail[];
}

interface SnoozeEmailData {
  snoozeEmail: SnoozedEmail;
}

interface UpdateSnoozeData {
  updateSnooze: SnoozedEmail;
}

interface UnsnoozeEmailData {
  unsnoozeEmail: boolean;
}

const DEFAULT_SNOOZED_EMAILS_FETCH_POLICY = FETCH_POLICY_CACHE_FIRST;

export function useGetSnoozedEmails(
  fetchPolicy: WatchQueryFetchPolicy = DEFAULT_SNOOZED_EMAILS_FETCH_POLICY,
) {
  const { data, loading, error, refetch } = useQuery<SnoozedEmailsData>(
    GET_SNOOZED_EMAILS,
    {
      fetchPolicy,
      nextFetchPolicy: FETCH_POLICY_CACHE_FIRST,
    },
  );

  return {
    snoozedEmails: data?.snoozedEmails ?? [],
    loading,
    error,
    refetch,
  };
}

export function useSnoozeEmail() {
  const [mutate, { loading, error }] = useMutation<SnoozeEmailData>(
    SNOOZE_EMAIL,
    {
      refetchQueries: [{ query: GET_SNOOZED_EMAILS }],
      awaitRefetchQueries: true,
    },
  );

  const snoozeEmail = async (input: SnoozeEmailInput) => {
    const { data } = await mutate({ variables: { input } });
    return data?.snoozeEmail ?? null;
  };

  return { snoozeEmail, loading, error };
}

export function useUpdateSnooze() {
  const [mutate, { loading, error }] = useMutation<UpdateSnoozeData>(
    UPDATE_SNOOZE,
    {
      refetchQueries: [{ query: GET_SNOOZED_EMAILS }],
      awaitRefetchQueries: true,
    },
  );

  const updateSnooze = async (input: UpdateSnoozeInput) => {
    const { data } = await mutate({ variables: { input } });
    return data?.updateSnooze ?? null;
  };

  return { updateSnooze, loading, error };
}

export function useUnsnoozeEmail() {
  const [mutate, { loading, error }] = useMutation<UnsnoozeEmailData>(
    UNSNOOZE_EMAIL,
    {
      refetchQueries: [{ query: GET_SNOOZED_EMAILS }],
      awaitRefetchQueries: true,
    },
  );

  const unsnoozeEmail = async (snoozeId: string) => {
    const { data } = await mutate({ variables: { snoozeId } });
    return data?.unsnoozeEmail ?? false;
  };

  return { unsnoozeEmail, loading, error };
}
